// libs
import React, { useCallback, useMemo } from 'react';
import c from 'classnames';

import { BaseMenuItem, BaseMenuList, Box, Divider, Typography } from '@lyearn/ui';

import { blockType } from '../../types/menus';
import { addMenulistItemTypography, menuItemSelectedStyle } from './style';
import { AddMenuItemsProps, caption, divider } from './types';
import { useRenderBlocks } from './useRenderBlocks';
import { getBlocksFromMap } from './utils';

function AddMenuItems(props: AddMenuItemsProps) {
  const { blocksMap, onClick, selectedMenuItemIndex, autoFocus } = props;

  const availableBlocks = useMemo(() => getBlocksFromMap(blocksMap), [blocksMap]);

  const onItemClick = useCallback(
    (event: any) => {
      const index = event.currentTarget.getAttribute('data-index');
      const block = availableBlocks[index];

      onClick?.(block);
    },
    [availableBlocks, onClick],
  );

  const renderBlocks = useRenderBlocks(blocksMap);

  let blockIndex = -1;

  return (
    <BaseMenuList
      autoFocus={autoFocus}
      className="rounded-4 bg-bg-primary p-8 shadow-01 outline-none xs:px-0">
      {renderBlocks.map((item: blockType | caption | divider, index) => {
        // Don't render the last divider.
        if (renderBlocks.length === index + 1) {
          return null;
        }
        // For the typescript check.
        const captionItem = (item as caption)?.caption ? (item as caption).caption : undefined;
        const dividerItem = (item as divider)?.divider ? (item as divider).divider : undefined;
        const blockItem = !captionItem && !dividerItem ? (item as blockType) : undefined;
        // For the menu item selection, we don't want to consider the block type
        // headings. So we are keeping track of another counter and setting the
        // selection accordingly.
        if (blockItem) {
          blockIndex++;
        }

        return captionItem ? (
          <Typography
            key={captionItem}
            className="m-8 block text-ellipsis text-text-placeholder outline-none"
            data-id={captionItem.toLocaleUpperCase()}
            variant="label-caps">
            {captionItem.toLocaleUpperCase()}
          </Typography>
        ) : dividerItem ? (
          <Divider key={index} className="mx-8 my-16" color="border-interactive-secondary" />
        ) : blockItem ? (
          <BaseMenuItem
            key={index}
            className={c(
              'my-4 flex h-56 items-center rounded-8 p-8 hover:bg-s-info-light',
              menuItemSelectedStyle,
            )}
            data-id={blockItem.title}
            data-index={blockIndex}
            selected={blockIndex === selectedMenuItemIndex}
            onClick={onItemClick}>
            <blockItem.addMenuIcon className="h-48 w-48 text-icon-active" />
            <Box className={c('w-full overflow-hidden', addMenulistItemTypography)}>
              <Typography className="text-label-03 text-ellipsis" variant="body-short-02">
                {blockItem.title}
              </Typography>
              <Typography className="text-ellipsis text-text-placeholder" variant="body-short-03">
                {blockItem.description}
              </Typography>
            </Box>
          </BaseMenuItem>
        ) : null;
      })}
    </BaseMenuList>
  );
}

export default React.memo(AddMenuItems);
